import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../environments/environment';
import { ClienteModel } from '../core/models/api/cliente.model';
import { CommessaModel } from '../core/models/api/commessa.model';


@Injectable({
  providedIn: 'root',
})
export class RequisitiService {
  apiUrl: string = environment.backend.baseUrl + "/requisiti";

  constructor(
    private http: HttpClient,
    private logger: NGXLogger
  ) {}

  getClienti(): Observable<ClienteModel[]> {
    return this.http.get<ClienteModel[]>(this.apiUrl + "/clienti").pipe(
      tap(clienti => this.logger.debug('[REQUISITI-SERVICE] Clienti caricati:', clienti.length)),
      catchError(error => {
        this.logger.error('[REQUISITI-SERVICE] Errore nel caricamento dei clienti:', error);
        // Non restituire una lista vuota: un elenco vuoto è indistinguibile da
        // "nessun cliente presente" e maschera sessioni scadute o problemi di rete.
        return throwError(() => error);
      })
    );
  }

  getCommesse(): Observable<CommessaModel[]> {
    return this.http.get<CommessaModel[]>(this.apiUrl + "/commesse").pipe(
      tap(commesse => this.logger.debug('[REQUISITI-SERVICE] Commesse caricate:', commesse.length)),
      catchError(error => {
        this.logger.error('[REQUISITI-SERVICE] Errore nel caricamento delle commesse:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea un nuovo cliente
   */
  createCliente(cliente: Omit<ClienteModel, 'id'>): Observable<ClienteModel> {
    return this.http.post<ClienteModel>(`${this.apiUrl}/clienti`, cliente).pipe(
      tap(nuovoCliente => this.logger.debug('[REQUISITI-SERVICE] Cliente creato:', nuovoCliente)),
      catchError(error => {
        this.logger.error('[REQUISITI-SERVICE] Errore nella creazione del cliente:', error);
        throw error;
      })
    );
  }

  /**
   * Aggiorna la ragione sociale di un cliente
   */
  updateCliente(id: number, ragioneSociale: string): Observable<ClienteModel> {
    return this.http.patch<ClienteModel>(`${this.apiUrl}/clienti/${id}`, { ragioneSociale }).pipe(
      tap(clienteAggiornato => this.logger.debug('[REQUISITI-SERVICE] Cliente aggiornato:', clienteAggiornato)),
      catchError(error => {
        this.logger.error('[REQUISITI-SERVICE] Errore nell\'aggiornamento del cliente:', error);
        throw error;
      })
    );
  }

  /**
   * Elimina un cliente
   */
  deleteCliente(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/clienti/${id}`).pipe(
      tap(() => this.logger.debug('[REQUISITI-SERVICE] Cliente eliminato:', id)),
      catchError(error => {
        this.logger.error('[REQUISITI-SERVICE] Errore nell\'eliminazione del cliente:', error);
        throw error;
      })
    );
  }

  /**
   * Crea una nuova commessa
   */
  createCommessa(commessa: Omit<CommessaModel, 'id' | 'clienteRagioneSociale'>): Observable<CommessaModel> {
    return this.http.post<CommessaModel>(`${this.apiUrl}/commesse`, commessa).pipe(
      tap(nuovaCommessa => this.logger.debug('[REQUISITI-SERVICE] Commessa creata:', nuovaCommessa)),
      catchError(error => {
        this.logger.error('[REQUISITI-SERVICE] Errore nella creazione della commessa:', error);
        throw error;
      })
    );
  }

  /**
   * Aggiorna una commessa
   */
  updateCommessa(id: number, data: { codice?: string; descrizione?: string; clienteId?: number; chiusa?: boolean }): Observable<CommessaModel> {
    return this.http.patch<CommessaModel>(`${this.apiUrl}/commesse/${id}`, data).pipe(
      tap(commessaAggiornata => this.logger.debug('[REQUISITI-SERVICE] Commessa aggiornata:', commessaAggiornata)),
      catchError(error => {
        this.logger.error('[REQUISITI-SERVICE] Errore nell\'aggiornamento della commessa:', error);
        throw error;
      })
    );
  }

  /**
   * Elimina una commessa
   */
  deleteCommessa(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/commesse/${id}`).pipe(
      tap(() => this.logger.debug('[REQUISITI-SERVICE] Commessa eliminata:', id)),
      catchError(error => {
        this.logger.error('[REQUISITI-SERVICE] Errore nell\'eliminazione della commessa:', error);
        throw error;
      })
    );
  }

}
